// UI Components - standalone versions for access-registration
import React from 'react';
import styled, { css } from 'styled-components';
import { Colors, Spacing, Dimensions, fontStyles } from './theme';
import { BorisScreener } from './borisScreenerTheme';

// ============ Typography ============

type TextSizes = 'p1' | 'p2' | 'p3' | 'p4' | 'p5';
type TitleSizes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface TypographyProps {
  bold?: boolean;
  inverse?: boolean;
}

export interface TextProps extends TypographyProps {
  size?: TextSizes | TitleSizes;
  centered?: boolean;
  disabled?: boolean;
  underline?: boolean;
  children?: React.ReactNode;
}

export interface TitleProps extends TypographyProps {
  size?: TitleSizes;
  centered?: boolean;
  margin?: string;
  underline?: boolean;
  children?: React.ReactNode;
}

const textStyles = ({
  size = 'p1',
  bold = false,
  inverse = false,
}: {
  size?: string;
  bold?: boolean;
  inverse?: boolean;
}) => css`
  font-family: ${bold ? fontStyles.bold.fontFamily : fontStyles[size]?.fontFamily || 'Lato'};
  font-size: ${fontStyles[size]?.fontSize || 18}px;
  font-weight: ${bold ? 700 : 400};
  color: ${inverse ? fontStyles.inverse.color : fontStyles[size]?.color || Colors.foreground};
  line-height: ${fontStyles[size]?.lineHeight ? `${fontStyles[size].lineHeight}px` : 'inherit'};
`;

export const Text = styled.span<TextProps>`
  ${({ size, bold, inverse }) => textStyles({ size, bold, inverse })}
  text-align: ${({ centered }) => (centered ? 'center' : 'left')};
  text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
`;

export const Title = styled.h1<TitleProps>`
  ${({ size = 'h1', bold, inverse }) => textStyles({ size, bold, inverse })}
  text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
  text-align: ${({ centered }) => (centered ? 'center' : 'left')};
  margin: ${({ margin }) => margin || '0'};
`;

// ============ Layout Components ============

export const VStack = styled.div<{ gap?: number; justify?: 'start' | 'end' | 'center' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ justify }) => justify || 'center'};
  width: 100%;
  gap: ${(props) => props.gap ?? 0}px;
`;

export const HStack = styled.div<{ gap?: number; justify?: 'start' | 'end' | 'center' }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ justify }) => justify || 'center'};
  width: 100%;
  gap: ${(props) => props.gap ?? 0}px;
`;

export const WrappedHStack = styled.div<{ gap?: number; span?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: ${(props) => props.gap ?? 0}px;
  flex-wrap: wrap;
`;

export const Column = styled.div<{ span?: number; marginTop?: number; marginBottom?: number }>`
  width: ${(props) => (props.span ?? 12) * 80 - Spacing.x2}px;
  max-width: 100%;

  margin-top: ${(props) => props.marginTop ?? Spacing.x2}px;
  margin-bottom: ${(props) => props.marginBottom ?? 0}px;

  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    width: 100%;
  }
`;

export const CenteredColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const GenericContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

// ============ Button ============

type ButtonVariant =
  | 'action-primary'
  | 'action-secondary'
  | 'action-tertiary'
  | 'brand-primary'
  | 'brand-secondary'
  | 'brand-tertiary'
  | 'loading'
  | 'disabled';

type ButtonSize = 'small' | 'xsmall' | 'default';

export interface ButtonProps {
  text: string;
  onClick: () => void;
  onDisabledClick?: () => void;
  type?: 'submit' | 'button';
  size?: ButtonSize;
  variant?: ButtonVariant;
}

const BACKGROUND_COLORS: Record<ButtonVariant, { default: string; pressed: string }> = {
  'action-primary': { default: BorisScreener.btnPrimary, pressed: BorisScreener.btnPrimaryHover },
  'action-secondary': { default: Colors.primaryPale, pressed: Colors.primaryLighter },
  'action-tertiary': { default: 'transparent', pressed: 'transparent' },
  'brand-primary': { default: BorisScreener.btnPrimary, pressed: BorisScreener.btnPrimaryHover },
  'brand-secondary': { default: Colors.brandSecondary, pressed: Colors.brandSecondaryDark },
  'brand-tertiary': { default: 'transparent', pressed: 'transparent' },
  // Boris `.dsb:disabled` keeps the base color and just drops to opacity 0.38
  // (a faded sage-green for the primary CTA), rather than a flat gray fill.
  disabled: { default: BorisScreener.btnPrimary, pressed: BorisScreener.btnPrimary },
  loading: { default: BorisScreener.btnPrimaryHover, pressed: BorisScreener.btnPrimaryHover },
};

const TEXT_COLORS: Record<
  ButtonVariant | 'action-tertiary-pressed' | 'brand-tertiary-pressed',
  string
> = {
  'action-primary': Colors.background,
  'action-secondary': Colors.primary,
  'action-tertiary': Colors.primary,
  'brand-primary': Colors.background,
  'brand-secondary': Colors.foreground,
  'brand-tertiary': Colors.brand,
  'action-tertiary-pressed': Colors.actionTertiaryPressed,
  'brand-tertiary-pressed': Colors.brandTertiaryPressed,
  disabled: Colors.background,
  loading: Colors.primary,
};

const paddingMap: Record<ButtonSize, string> = {
  small: `${Spacing.x2}px ${Spacing.x3}px`,
  xsmall: `${Spacing.x1}px ${Spacing.x2}px`,
  default: `${Spacing.x2}px ${Spacing.x4}px`,
};

const ButtonWithStyle = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  font-family: ${fontStyles.buttonLarge.fontFamily};
  font-weight: ${fontStyles.buttonLarge.fontWeight};
  /* Boris design-system buttons (.dsb): l = 72px / 22px, m = 56px / 18px, s = 40px / 16px. */
  font-size: ${({ $size }) => ($size === 'small' ? 18 : $size === 'xsmall' ? 16 : 22)}px;
  height: ${({ $size }) => ($size === 'default' ? 72 : $size === 'small' ? 56 : 40)}px;
  padding: ${({ $size }) => paddingMap[$size]};
  background-color: ${({ $variant }) => BACKGROUND_COLORS[$variant].default};
  border-radius: ${({ $size }) => ($size === 'default' ? 32 : $size === 'small' ? 24 : 16)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: ${({ $variant }) => ($variant === 'disabled' ? 'default' : 'pointer')};
  width: 100%;
  border-width: 0;
  color: ${({ $variant }) => TEXT_COLORS[$variant]};
  /* Boris .dsb:disabled drops to opacity 0.38 (base color faded), not a gray swap. */
  opacity: ${({ $variant }) => ($variant === 'disabled' ? 0.38 : 1)};

  &:active,
  &:hover,
  &:focus {
    background-color: ${({ $variant }) => BACKGROUND_COLORS[$variant].pressed};
    ${({ $variant }) =>
      $variant === 'action-tertiary' || $variant === 'brand-tertiary'
        ? `color: ${TEXT_COLORS[`${$variant}-pressed` as keyof typeof TEXT_COLORS]};`
        : ''}
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${Colors.background};
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'action-primary',
  size = 'default',
  text,
  onClick,
  onDisabledClick,
  type = 'button',
}) => {
  const handleClick = () => {
    if (!['disabled', 'loading'].includes(variant)) {
      onClick();
    } else if (onDisabledClick) {
      onDisabledClick();
    }
  };

  return (
    <ButtonWithStyle
      onClick={handleClick}
      $variant={variant}
      $size={size}
      role="button"
      type={type}
      disabled={false}
      aria-disabled={variant === 'disabled'}
    >
      {variant === 'loading' ? <LoadingSpinner /> : text}
    </ButtonWithStyle>
  );
};

// ============ TipBox ============

interface TipBoxProps {
  children: React.ReactNode;
  width?: string;
}

export const UList = styled.ul`
  margin-top: ${Spacing.x1}px;
  margin-bottom: 0px;
  padding: 0px 0px 0px ${Spacing.x1}px;
`;

const TipBoxContainer = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: ${Colors.backgroundAlt};
  border-radius: ${Spacing.x1}px;
  padding: ${Spacing.x2}px;
  width: ${(props) => props.width || 'auto'};
  box-sizing: border-box;
  > :not(:last-child) {
    margin-bottom: ${Spacing.x2}px;
  }
`;

export const TipBox: React.FC<TipBoxProps> = ({ children, width }) => (
  <TipBoxContainer width={width}>{children}</TipBoxContainer>
);
